import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';
import './NovosUsuarios.css';
import {
  FaUserPlus, FaBuilding, FaFileDownload, FaFileUpload,
  FaFileExport, FaExclamationTriangle, FaShieldAlt, FaBriefcase,
  FaTimes, FaCheckCircle, FaTimesCircle, FaSpinner, FaSyncAlt,
  FaEnvelope, FaWhatsapp, FaBan, FaCopy, FaKey, FaCog, FaSearch
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

/* ─────────────────────────────────────────────────────────────
   Barra de licenças
   ───────────────────────────────────────────────────────────── */
function LicenseBar({ clienteId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clienteId) { setInfo(null); return; }

    let cancelled = false;
    setLoading(true);
    api.get(`/api/usuarios/licencas/cliente/${clienteId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const keys = Array.isArray(data) ? data : (data?.items || []);
        const total = keys.length;
        const used = keys.filter(k => k.usuario_id_alocado).length;
        setInfo({ total, used, free: total - used });
      })
      .catch(() => { if (!cancelled) setInfo(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [clienteId]);

  if (!clienteId) return null;

  if (loading) {
    return (
      <div className="nu-license-bar nu-license-loading">
        <FaSpinner className="nu-spin" /> Carregando licenças…
      </div>
    );
  }

  if (!info) return null;

  const pct = info.total > 0 ? Math.round((info.used / info.total) * 100) : 0;
  const tier = pct >= 100 ? 'full' : pct >= 80 ? 'warn' : 'ok';
  const tierMap = {
    ok: { label: 'Vagas disponíveis', color: '#10b981' },
    warn: { label: 'Quase no limite', color: '#f59e0b' },
    full: { label: 'Limite atingido', color: '#ef4444' },
  };
  const { label, color } = tierMap[tier];

  return (
    <div className={`nu-license-bar nu-license-${tier}`}>
      <div className="nu-license-bar__top">
        <div className="nu-license-bar__stats">
          <span className="nu-license-bar__num" style={{ color }}>
            {tier === 'full' ? <FaTimesCircle /> : <FaCheckCircle />}
            {info.free} vaga{info.free !== 1 ? 's' : ''} livre{info.free !== 1 ? 's' : ''}
          </span>
          <span className="nu-license-bar__detail">
            {info.used} de {info.total} licenças utilizadas — {label}
          </span>
        </div>
        <span className="nu-license-bar__pct" style={{ color }}>{pct}%</span>
      </div>

      <div className="nu-license-bar__track">
        <div className="nu-license-bar__fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>

      <div className="nu-license-bar__slots">
        {Array.from({ length: Math.min(info.total, 30) }).map((_, i) => (
          <div key={i} className={`nu-slot ${i < info.used ? 'used' : 'free'}`}
            style={{ background: i < info.used ? color : undefined }}
            title={i < info.used ? 'Licença em uso' : 'Licença disponível'} />
        ))}
        {info.total > 30 && <span className="nu-slot-more">+{info.total - 30}</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Seletor de canal de envio (reutilizado no unitário e no bulk)
   ───────────────────────────────────────────────────────────── */
function CanalEnvioSelector({ canal, setCanal, telefoneDisponivel }) {
  return (
    <div className="nu-field col-2">
      <label>ENVIAR SENHA POR</label>
      <div className="nu-channel-group">
        <button type="button" className={`nu-channel-card ${canal === 'email' ? 'active' : ''}`} onClick={() => setCanal('email')}>
          <FaEnvelope /> E-mail
        </button>
        <button type="button" className={`nu-channel-card ${canal === 'whatsapp' ? 'active' : ''}`} onClick={() => setCanal('whatsapp')}>
          <FaWhatsapp /> WhatsApp
        </button>
        <button type="button" className={`nu-channel-card ${canal === 'manual' ? 'active' : ''}`} onClick={() => setCanal('manual')}>
          <FaBan /> Não enviar
        </button>
      </div>
      {canal === 'whatsapp' && !telefoneDisponivel && (
        <p className="nu-channel-warning"><FaExclamationTriangle /> Informe o telefone para envio via WhatsApp.</p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Painel de opções de complexidade de senha
   ───────────────────────────────────────────────────────────── */
function OpcoesSenhaPanel({ opcoes, onToggle, onLengthChange }) {
  return (
    <div className="nu-pass-options">
      <label className={opcoes.maiusculas ? 'active' : ''}>
        <input type="checkbox" checked={opcoes.maiusculas} onChange={() => onToggle('maiusculas')} /> Maiúsculas
      </label>
      <label className={opcoes.minusculas ? 'active' : ''}>
        <input type="checkbox" checked={opcoes.minusculas} onChange={() => onToggle('minusculas')} /> Minúsculas
      </label>
      <label className={opcoes.numeros ? 'active' : ''}>
        <input type="checkbox" checked={opcoes.numeros} onChange={() => onToggle('numeros')} /> Números
      </label>
      <label className={opcoes.simbolos ? 'active' : ''}>
        <input type="checkbox" checked={opcoes.simbolos} onChange={() => onToggle('simbolos')} /> Símbolos
      </label>
      <div className="nu-pass-length">
        <span>Tamanho: <strong>{opcoes.tamanho}</strong></span>
        <input type="range" min="8" max="24" value={opcoes.tamanho}
          onChange={(e) => onLengthChange(Number(e.target.value))} />
      </div>
    </div>
  );
}

function forcaSenha({ tamanho, maiusculas, minusculas, numeros, simbolos }) {
  const setsAtivos = [maiusculas, minusculas, numeros, simbolos].filter(Boolean).length;
  if (tamanho >= 16 && setsAtivos >= 4) return { label: 'Muito forte', color: '#10b981' };
  if (tamanho >= 12 && setsAtivos >= 3) return { label: 'Forte', color: '#22c55e' };
  if (tamanho >= 10 && setsAtivos >= 2) return { label: 'Boa', color: '#f59e0b' };
  return { label: 'Fraca', color: '#ef4444' };
}

/* ─────────────────────────────────────────────────────────────
   Componente principal
   ───────────────────────────────────────────────────────────── */
const NovosUsuarios = ({
  onClose,
  onSuccess,
  perfilLogado,
  allClients,
  usuarios,
  limitReached,
  setIsLicenseModalOpen,
  licenseInfo,
}) => {
  const importInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nome: '', email: '', senha: '', telefone: '',
    perfil: 'operacional', matricula: '', cpf: '',
    filial: '', cargo: '', centroDeCusto: '', cliente_id: '',
  });

  // ── Senha unitária ──
  const [modoSenha, setModoSenha] = useState('manual'); // 'manual' | 'auto'
  const [canalEnvio, setCanalEnvio] = useState('email');
  const [gerandoSenha, setGerandoSenha] = useState(false);
  const [opcoesSenha, setOpcoesSenha] = useState({
    tamanho: 12, maiusculas: true, minusculas: true, numeros: true, simbolos: true,
  });

  // ── Senha em massa (importação) ──
  const [painelImportacaoAberto, setPainelImportacaoAberto] = useState(false);
  const [gerarSenhaImportacao, setGerarSenhaImportacao] = useState(true);
  const [canalEnvioImportacao, setCanalEnvioImportacao] = useState('email');
  const [opcoesSenhaImportacao, setOpcoesSenhaImportacao] = useState({
    tamanho: 12, maiusculas: true, minusculas: true, numeros: true, simbolos: true,
  });
  const [importando, setImportando] = useState(false);

  // ── Seletor de empresa (admin_geral) ──
  const [clientePickerAberto, setClientePickerAberto] = useState(false);
  const [clienteBusca, setClienteBusca] = useState('');

  const loggedInClientId = allClients.find(c => c.nome_empresa === licenseInfo?.clientName)?.id;
  const targetClientId = perfilLogado === 'admin_geral' ? formData.cliente_id : loggedInClientId;
  const showImportExport = !!targetClientId || perfilLogado === 'admin';

  const clienteSelecionado = allClients.find(c => String(c.id) === String(formData.cliente_id)) || null;
  const clientesFiltrados = clienteBusca.trim()
    ? allClients.filter(c => (c.nome_empresa || '').toLowerCase().includes(clienteBusca.trim().toLowerCase()))
    : allClients;

  const fecharClientePicker = () => { setClientePickerAberto(false); setClienteBusca(''); };
  const selecionarCliente = (id) => {
    setFormData(prev => ({ ...prev, cliente_id: String(id) }));
    fecharClientePicker();
  };

  const handleInputChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  /* ── Geração de senha (unitária) ── */
  const gerarNovaSenha = async () => {
    setGerandoSenha(true);
    try {
      const { data } = await api.post('/api/usuarios/senha/gerar', opcoesSenha);
      setFormData(prev => ({ ...prev, senha: data.senha }));
    } catch {
      toast.error('Não foi possível gerar a senha.');
    } finally {
      setGerandoSenha(false);
    }
  };

  useEffect(() => {
    if (modoSenha === 'auto') gerarNovaSenha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoSenha]);

  const toggleOpcaoSenha = (key) => {
    setOpcoesSenha(prev => {
      const proximo = { ...prev, [key]: !prev[key] };
      const algumaAtiva = proximo.maiusculas || proximo.minusculas || proximo.numeros || proximo.simbolos;
      if (!algumaAtiva) {
        toast.warn('Mantenha ao menos uma opção de complexidade ativa.');
        return prev;
      }
      return proximo;
    });
  };

  // Regenera automaticamente ao ajustar as opções (feedback imediato)
  useEffect(() => {
    if (modoSenha === 'auto') gerarNovaSenha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opcoesSenha]);

  const copiarSenha = () => {
    navigator.clipboard.writeText(formData.senha);
    toast.info('Senha copiada para a área de transferência.');
  };

  /* ── Cadastro manual ── */
  const handleRegister = async (e) => {
    e.preventDefault();

    if (perfilLogado === 'admin_geral' && !formData.cliente_id) {
      toast.error('Selecione a empresa de destino antes de cadastrar.');
      setClientePickerAberto(true);
      return;
    }

    if (canalEnvio === 'whatsapp' && !formData.telefone) {
      toast.error('Informe o telefone do colaborador para enviar a senha por WhatsApp.');
      return;
    }

    try {
      const payload = {
        ...formData,
        canalEnvioSenha: canalEnvio,
      };

      const { data } = await api.post('/api/usuarios/register', payload);

      if (data.envioCredenciais?.canal) {
        const canalLabel = data.envioCredenciais.canal === 'email' ? 'e-mail' : 'WhatsApp';
        toast.success(`Usuário ${formData.nome} cadastrado! Senha enviada por ${canalLabel}.`);
      } else if (data.envioCredenciais?.erro) {
        toast.warn(`Usuário criado, mas houve falha no envio da senha: ${data.envioCredenciais.erro}`);
      } else {
        toast.success(`Usuário ${formData.nome} adicionado com sucesso!`);
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(`Erro ao adicionar: ${error.response?.data?.error || 'Tente novamente.'}`);
    }
  };

  /* ── Exportar / Modelo ── */
  const handleExport = () => {
    if (usuarios.length === 0) return toast.warn('Nada para exportar.');
    const headers = ['Nome','Email','Telefone','Perfil','Matrícula','CPF','Filial','Cargo','Centro de Custo','Status'];
    const rows = usuarios.map(u => [
      u.nome, u.email, u.telefone || '', u.perfil,
      u.matricula || '', u.cpf || '', u.filial || '',
      u.cargo || '', u.centroDeCusto || '', u.status,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
    XLSX.writeFile(wb, `export_usuarios_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`);
  };

  const handleDownloadTemplate = () => {
    const headers = [['Nome','Email','Telefone','Perfil','Matrícula','CPF','Filial','Cargo','Centro de Custo']];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Modelo');
    XLSX.writeFile(wb, 'modelo_importacao_usuarios.xlsx');
  };

  /* ── Importação via XLSX ── */
  const processImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!targetClientId) {
      toast.warn('Selecione um cliente antes de importar.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      setImportando(true);
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
        if (json.length === 0) return toast.error('Arquivo vazio ou inválido.');

        const res = await api.post('/api/usuarios/import', {
          users: json,
          cliente_id: targetClientId,
          gerarSenhaAutomaticamente: gerarSenhaImportacao,
          opcoesSenha: opcoesSenhaImportacao,
          canalEnvioSenha: canalEnvioImportacao,
        });

        const { successCount, errorCount, envios } = res.data;
        const enviosOk = (envios || []).filter(en => en.status === 'ok').length;
        const enviosFalha = (envios || []).filter(en => en.status === 'falha').length;

        let msg = `${successCount} usuário(s) importado(s)!`;
        if (errorCount > 0) msg += ` ${errorCount} com erro.`;
       if (canalEnvioImportacao !== 'manual' && envios?.length) {
          msg += ` ${enviosOk} senha(s) enviada(s)`;
          if (enviosFalha) msg += `, ${enviosFalha} falha(s) no envio`;
          msg += '.';
        }

        toast.success(msg);
        onSuccess();
        onClose();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Erro no processamento da importação.');
      } finally {
        setImportando(false);
        e.target.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const forca = forcaSenha(modoSenha === 'auto' ? opcoesSenha : { tamanho: 0, maiusculas: false, minusculas: false, numeros: false, simbolos: false });

  return (
    <div className="nu-overlay" onMouseDown={(e) => { if (e.target.classList.contains('nu-overlay')) onClose(); }}>
      <div className="nu-modal">

        {/* ── HEADER ── */}
        <header className="nu-header">
          <div className="nu-header-left">
            <div className="nu-icon-badge"><FaUserPlus /></div>
            <div>
              <h2 className="nu-title">Novo Colaborador</h2>
              <p className="nu-subtitle">Preencha os dados para cadastrar manualmente</p>
            </div>
          </div>

          <div className="nu-header-right">
            {showImportExport && (
              <div className="nu-io-group">
                <button type="button" onClick={handleDownloadTemplate} className="nu-io-btn model" title="Baixar modelo XLSX">
                  <FaFileDownload /> <span>Modelo</span>
                </button>
                <button type="button" onClick={() => setPainelImportacaoAberto(v => !v)} className="nu-io-btn import" title="Configurar e importar XLSX">
                  <FaFileUpload /> <span>Importar</span>
                </button>
                <button type="button" onClick={handleExport} className="nu-io-btn export" title="Exportar lista atual">
                  <FaFileExport /> <span>Exportar</span>
                </button>
              </div>
            )}
            <button type="button" onClick={onClose} className="nu-close" aria-label="Fechar">
              <FaTimes />
            </button>
          </div>
        </header>

        <form onSubmit={handleRegister} className="nu-form" autoComplete="off">
          <div className="nu-body">

            {perfilLogado === 'admin' && limitReached && (
              <div className="nu-limit-overlay">
                <FaExclamationTriangle size={32} />
                <p>Limite de licenças atingido!</p>
                <button type="button" className="nu-limit-btn"
                  onClick={() => { onClose(); setIsLicenseModalOpen(true); }}>
                  Solicitar Upgrade
                </button>
              </div>
            )}

            {/* ── PAINEL DE IMPORTAÇÃO EM MASSA ── */}
            {painelImportacaoAberto && showImportExport && (
              <section className="nu-section nu-bulk-panel">
                <div className="nu-section-label">
                  <FaFileUpload /> IMPORTAÇÃO EM MASSA — SENHAS
                </div>

                <label className="nu-bulk-check">
                  <input type="checkbox" checked={gerarSenhaImportacao}
                    onChange={(e) => setGerarSenhaImportacao(e.target.checked)} />
                  Gerar senha automaticamente para quem não tiver senha na planilha
                </label>

                {gerarSenhaImportacao && (
                  <OpcoesSenhaPanel
                    opcoes={opcoesSenhaImportacao}
                    onToggle={(key) => setOpcoesSenhaImportacao(prev => {
                      const proximo = { ...prev, [key]: !prev[key] };
                      const algumaAtiva = proximo.maiusculas || proximo.minusculas || proximo.numeros || proximo.simbolos;
                      return algumaAtiva ? proximo : prev;
                    })}
                    onLengthChange={(tamanho) => setOpcoesSenhaImportacao(prev => ({ ...prev, tamanho }))}
                  />
                )}

                {/* Canal aplica a TODAS as linhas com senha utilizável:
                    senha gerada pelo sistema OU senha vinda da planilha. */}
                <CanalEnvioSelector
                  canal={canalEnvioImportacao}
                  setCanal={setCanalEnvioImportacao}
                  telefoneDisponivel
                />
                <p className="nu-bulk-hint">
                  💡 A planilha precisa ter a coluna <strong>Telefone</strong> preenchida para quem for receber por WhatsApp.
                </p>

                <button type="button" className="nu-bulk-confirm" disabled={importando}
                  onClick={() => importInputRef.current.click()}>
                  {importando ? <><FaSpinner className="nu-spin" /> Importando…</> : <><FaFileUpload /> Selecionar planilha e importar</>}
                </button>
              </section>
            )}

            {/* ── EMPRESA ── */}
            <section className="nu-section nu-section--company">
              <div className="nu-section-label">
                <FaBuilding /> {perfilLogado === 'admin_geral' ? 'EMPRESA DE DESTINO' : 'STATUS DAS LICENÇAS'}
              </div>

              {perfilLogado === 'admin_geral' && (
                <>
                  <button
                    type="button"
                    className={`nu-client-picker-btn ${clienteSelecionado ? 'selected' : ''}`}
                    onClick={() => setClientePickerAberto(true)}
                  >
                    <span className="nu-client-picker-btn__icon"><FaBuilding /></span>
                    <span className="nu-client-picker-btn__label">
                      {clienteSelecionado ? clienteSelecionado.nome_empresa : 'Selecione o cliente…'}
                    </span>
                    <span className="nu-client-picker-btn__action">
                      {clienteSelecionado ? 'Alterar' : 'Escolher'}
                    </span>
                  </button>

                  {!formData.cliente_id && (
                    <p className="nu-channel-warning">
                      <FaExclamationTriangle /> Selecione o cliente para ver o status das licenças.
                    </p>
                  )}
                </>
              )}

              <LicenseBar clienteId={targetClientId} />
            </section>

            {/* ── DADOS DE ACESSO ── */}
            <section className="nu-section">
              <div className="nu-section-label"><FaShieldAlt /> DADOS DE ACESSO</div>

              <div className="nu-grid">
                <div className="nu-field col-2">
                  <label>NOME COMPLETO</label>
                  <input type="text" name="nome" value={formData.nome}
                    onChange={handleInputChange} required placeholder="Ex: João Silva" />
                </div>
                <div className="nu-field">
                  <label>E-MAIL PROFISSIONAL</label>
                  <input type="email" name="email" value={formData.email}
                    onChange={handleInputChange} required placeholder="joao@empresa.com"
                    autoComplete="off" />
                </div>
                <div className="nu-field">
                  <label>TELEFONE / WHATSAPP</label>
                  <input type="tel" name="telefone" value={formData.telefone}
                    onChange={handleInputChange} placeholder="(00) 00000-0000" />
                </div>

                {/* ── SENHA ── */}
                <div className="nu-field col-2">
                  <label>SENHA DE ACESSO</label>
                  <div className="nu-pass-toggle">
                    <button type="button" className={modoSenha === 'manual' ? 'active' : ''} onClick={() => setModoSenha('manual')}>
                      <FaKey /> Definir manualmente
                    </button>
                    <button type="button" className={modoSenha === 'auto' ? 'active' : ''} onClick={() => setModoSenha('auto')}>
                      <FaCog /> Gerar automaticamente
                    </button>
                  </div>

                  {modoSenha === 'manual' ? (
                    <input type="password" name="senha" value={formData.senha}
                      onChange={handleInputChange} required minLength="6" placeholder="Mínimo 6 caracteres"
                      autoComplete="new-password" />
                  ) : (
                    <div className="nu-pass-generator">
                      <div className="nu-pass-generator__display">
                        <code>{formData.senha || '••••••••••••'}</code>
                        <span className="nu-pass-strength" style={{ color: forca.color }}>{forca.label}</span>
                        <button type="button" className="nu-pass-icon-btn" onClick={copiarSenha} title="Copiar senha">
                          <FaCopy />
                        </button>
                        <button type="button" className={`nu-pass-icon-btn nu-pass-refresh ${gerandoSenha ? 'spinning' : ''}`}
                          onClick={gerarNovaSenha} title="Gerar outra senha">
                          <FaSyncAlt />
                        </button>
                      </div>
                      <OpcoesSenhaPanel
                        opcoes={opcoesSenha}
                        onToggle={toggleOpcaoSenha}
                        onLengthChange={(tamanho) => setOpcoesSenha(prev => ({ ...prev, tamanho }))}
                      />
                    </div>
                  )}
                </div>

                <CanalEnvioSelector canal={canalEnvio} setCanal={setCanalEnvio} telefoneDisponivel={!!formData.telefone} />

                <div className="nu-field">
                  <label>NÍVEL DE PERMISSÃO</label>
                  <select name="perfil" value={formData.perfil} onChange={handleInputChange}>
                    {perfilLogado === 'admin_geral' && <option value="admin_geral">Admin Geral (Sistema)</option>}
                    <option value="admin">Administrador da Unidade</option>
                    <option value="operacional">Operacional / Pátio</option>
                    <option value="financeiro">Financeiro / Faturamento</option>
                    <option value="rh">Recursos Humanos</option>
                    <option value="conhecimento_manager">Gerente de Processos</option>
                  </select>
                </div>
              </div>
            </section>

            {/* ── DADOS CORPORATIVOS ── */}
            <section className="nu-section">
              <div className="nu-section-label"><FaBriefcase /> INFORMAÇÕES CORPORATIVAS</div>

              <div className="nu-grid">
                <div className="nu-field">
                  <label>MATRÍCULA</label>
                  <input type="text" name="matricula" value={formData.matricula} onChange={handleInputChange} />
                </div>
                <div className="nu-field">
                  <label>CPF</label>
                  <input type="text" name="cpf" value={formData.cpf} onChange={handleInputChange} placeholder="000.000.000-00" />
                </div>
                <div className="nu-field">
                  <label>FILIAL / UNIDADE</label>
                  <input type="text" name="filial" value={formData.filial} onChange={handleInputChange} />
                </div>
                <div className="nu-field">
                  <label>CARGO</label>
                  <input type="text" name="cargo" value={formData.cargo} onChange={handleInputChange} />
                </div>
                <div className="nu-field col-2">
                  <label>CENTRO DE CUSTO</label>
                  <input type="text" name="centroDeCusto" value={formData.centroDeCusto} onChange={handleInputChange} />
                </div>
              </div>
            </section>
          </div>

          <footer className="nu-footer">
            <button type="button" onClick={onClose} className="nu-btn-cancel">Cancelar</button>
            <button type="submit" className="nu-btn-submit">
              <FaUserPlus /> Cadastrar Usuário
            </button>
          </footer>
        </form>
      </div>

      <input type="file" accept=".xlsx,.xls" onChange={processImportFile}
        style={{ display: 'none' }} ref={importInputRef} />

      {clientePickerAberto && (
        <div
          className="nu-client-picker-overlay"
          onMouseDown={(e) => { if (e.target.classList.contains('nu-client-picker-overlay')) fecharClientePicker(); }}
        >
          <div className="nu-client-picker">
            <header className="nu-client-picker__header">
              <div className="nu-client-picker__title"><FaBuilding /> Selecionar empresa</div>
              <button type="button" className="nu-client-picker__close" onClick={fecharClientePicker} aria-label="Fechar">
                <FaTimes />
              </button>
            </header>

            <div className="nu-client-picker__search">
              <FaSearch />
              <input
                type="text"
                autoFocus
                placeholder="Buscar empresa pelo nome…"
                value={clienteBusca}
                onChange={(e) => setClienteBusca(e.target.value)}
              />
            </div>

            <div className="nu-client-picker__list">
              {clientesFiltrados.length === 0 ? (
                <div className="nu-client-picker__empty">Nenhuma empresa encontrada.</div>
              ) : clientesFiltrados.map(client => {
                const ativo = String(client.id) === String(formData.cliente_id);
                return (
                  <button
                    type="button"
                    key={client.id}
                    className={`nu-client-picker__item ${ativo ? 'active' : ''}`}
                    onClick={() => selecionarCliente(client.id)}
                  >
                    <span className="nu-client-picker__item-icon"><FaBuilding /></span>
                    <span className="nu-client-picker__item-name">{client.nome_empresa}</span>
                    {ativo && <FaCheckCircle className="nu-client-picker__item-check" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NovosUsuarios;